import type { RouteComponent } from 'vue-router';

export interface NavigationGroup {
	name: string;
	icon: string;
	subActivePaths: string;
}

export interface AdminModule {
	name: string;
	routePrefix: string;
	additionalGuardPrefixes?: readonly string[];
	permission: string;
	group: string;
	pages: readonly ModulePageRoute[];
}

export interface ModulePageRoute {
	path: string;
	name: string;
component: () => Promise<RouteComponent | { default: RouteComponent }>;
}

// Authenticated routes which intentionally do not belong to an Auth0 module.
// Keeping this list explicit lets tests detect newly added routes which would
// otherwise be unrestricted merely because they were omitted from the registry.
export const authenticatedUnrestrictedRoutePaths = ['/', '/dashboard'] as const;

export const navigationGroups: readonly NavigationGroup[] = [
	{ name: 'Shop', icon: 'si si-shop', subActivePaths: '/listeritem' },
	{ name: 'Promo', icon: 'si si-megaphone', subActivePaths: '/crosssell' },
	{ name: 'Products', icon: 'si si-energy', subActivePaths: '/backend/blocks' },
	{ name: 'Localisation', icon: 'si si-globe', subActivePaths: '/region' },
	{ name: 'Design Assets', icon: 'si si-picture', subActivePaths: '/sticker' },
	{ name: 'Themes', icon: 'si si-fire', subActivePaths: '/themecategories' },
	{ name: 'Discounts', icon: 'si si-layers', subActivePaths: '/discount' },
	{ name: 'Integrations', icon: 'si si-organization', subActivePaths: '/shop-integration' },
	{ name: 'Developer', icon: 'si si-wrench', subActivePaths: '/app-settings' },
] as const;

export const moduleRegistry: readonly AdminModule[] = [
	{ name: 'Badges', routePrefix: '/badges', permission: 'Badges', group: 'Shop', pages: [
		{ path: '/badges', name: 'Badges', component: () => import('views/BadgesPage.vue') },
		{ path: '/badges/:id', name: 'EditBadges', component: () => import('views/EditBadgePage.vue') },
	] },
	{ name: 'Lister Items', routePrefix: '/listeritem', permission: 'Lister Items', group: 'Shop', pages: [
		{ path: '/listeritem', name: 'ListerItems', component: () => import('views/ListerItemsPage.vue') },
	] },
	{ name: 'PDPs', routePrefix: '/pdp', permission: 'PDPs', group: 'Shop', pages: [
		{ path: '/pdp', name: 'Pdp', component: () => import('views/PDPsPage.vue') },
		{ path: '/pdp/:id', name: 'EditPdp', component: () => import('views/EditPDPPage.vue') },
	] },
	{ name: 'Cross Sell', routePrefix: '/crosssell', permission: 'Cross Sell', group: 'Promo', pages: [
		{ path: '/crosssell', name: 'CrossSell', component: () => import('views/CrossSellsPage.vue') },
		{ path: '/crosssell/:id', name: 'EditCrossSell', component: () => import('views/EditCrossSellPage.vue') },
	] },
	{ name: 'Upsells', routePrefix: '/upsell', permission: 'Upsells', group: 'Promo', pages: [
		{ path: '/upsell', name: 'Upsells', component: () => import('views/UpsellsPage.vue') },
	] },
	{ name: 'Bulk Discount', routePrefix: '/discount-bulk', permission: 'Bulk Discount', group: 'Promo', pages: [
		{ path: '/discount-bulk', name: 'BulkDiscount', component: () => import('views/BulkDiscountsPage.vue') },
		{ path: '/discount-bulk/:id', name: 'EditBulkDiscount', component: () => import('views/EditBulkDiscountPage.vue') },
	] },
	{ name: '2D Models', routePrefix: '/model2d', permission: '2D Models', group: 'Products', pages: [
		{ path: '/model2d', name: 'Model2d', component: () => import('views/Model2DsPage.vue') },
		{ path: '/model2d/:id', name: 'EditModel2d', component: () => import('views/EditModel2DPage.vue') },
	] },
	{ name: '3D Models', routePrefix: '/model3d', permission: '3D Models', group: 'Products', pages: [
		{ path: '/model3d', name: 'Model3Ds', component: () => import('views/Model3DsPage.vue') },
		{ path: '/model3d/:id', name: 'EditModel3D', component: () => import('views/EditModel3DPage.vue') },
	] },
	{ name: 'Flex Group', routePrefix: '/flexgroup', permission: 'Flex Group', group: 'Products', pages: [
		{ path: '/flexgroup', name: 'FlexGroup', component: () => import('views/FlexGroupPage.vue') },
		{ path: '/flexgroup/:id', name: 'EditFlexGroup', component: () => import('views/EditFlexGroupPage.vue') },
	] },
	{ name: 'Offering', routePrefix: '/offerings', permission: 'Offering', group: 'Products', pages: [
		{ path: '/offerings', name: 'offerings', component: () => import('views/OfferingsPage.vue') },
		{ path: '/offerings/:id', name: 'editOffering', component: () => import('views/EditOfferingPage.vue') },
	] },
	{ name: 'Offering Option', routePrefix: '/offeringoption', permission: 'Offering Option', group: 'Products', pages: [
		{ path: '/offeringoption', name: 'OfferingOption', component: () => import('views/OfferingOptionsPage.vue') },
		{ path: '/offeringoption/:id', name: 'EditOfferingOption', component: () => import('views/EditOfferingOptionPage.vue') },
	] },
	{
		name: 'Pricing',
		routePrefix: '/pricing',
		additionalGuardPrefixes: ['/showOrder', '/appuser'],
		permission: 'Pricing',
		group: 'Products',
		pages: [
			{ path: '/pricing', name: 'pricing', component: () => import('views/PricingPage.vue') },
		],
	},
	{ name: 'Shipping', routePrefix: '/shipping', permission: 'Shipping', group: 'Products', pages: [
		{ path: '/shipping', name: 'Shipping', component: () => import('views/ShippingPage.vue') },
	] },
	{ name: 'Regions', routePrefix: '/region', permission: 'Regions', group: 'Localisation', pages: [
		{ path: '/region', name: 'Region', component: () => import('views/RegionsPage.vue') },
		{ path: '/region/:id', name: 'EditRegion', component: () => import('views/EditRegionPage.vue') },
	] },
	{ name: 'Currencies', routePrefix: '/currency', permission: 'Currencies', group: 'Localisation', pages: [
		{ path: '/currency', name: 'Currency', component: () => import('views/CurrenciesPage.vue') },
		{ path: '/currency/:id', name: 'EditCurrency', component: () => import('views/EditCurrencyPage.vue') },
	] },
	{ name: 'Countries', routePrefix: '/country', permission: 'Countries', group: 'Localisation', pages: [
		{ path: '/country', name: 'Country', component: () => import('views/CountriesPage.vue') },
		{ path: '/country/:id', name: 'EditCountry', component: () => import('views/EditCountryPage.vue') },
	] },
	{ name: 'Hyperlinks', routePrefix: '/hyperlink', permission: 'Hyperlinks', group: 'Localisation', pages: [
		{ path: '/hyperlink', name: 'Hyperlinks', component: () => import('views/HyperlinksPage.vue') },
		{ path: '/hyperlink/:id', name: 'EditHyperlinks', component: () => import('views/EditHyperlinksPage.vue') },
	] },
	{ name: 'Stickers', routePrefix: '/sticker', permission: 'Stickers', group: 'Design Assets', pages: [
		{ path: '/sticker', name: 'Stickers', component: () => import('views/StickersPage.vue') },
		{ path: '/sticker/:id', name: 'EditSticker', component: () => import('views/EditStickerPage.vue') },
	] },
	{ name: 'Page Background', routePrefix: '/pagebg', permission: 'Page Background', group: 'Design Assets', pages: [
		{ path: '/pagebg', name: 'PageBg', component: () => import('views/PageBackgroundsPage.vue') },
		{ path: '/pagebg/:id', name: 'EditPageBg', component: () => import('views/EditPageBackgroundPage.vue') },
	] },
	{ name: 'Photoframes', routePrefix: '/photoframe', permission: 'Photoframes', group: 'Themes', pages: [
		{ path: '/photoframe', name: 'Photoframe', component: () => import('views/PhotoframesPage.vue') },
		{ path: '/photoframe/:id', name: 'editPhotoframe', component: () => import('views/EditPhotoframePage.vue') },
	] },
	{ name: 'Layouts', routePrefix: '/layout', permission: 'Layouts', group: 'Themes', pages: [
		{ path: '/layout', name: 'Layout', component: () => import('views/LayoutsPage.vue') },
		{ path: '/layout/:id', name: 'EditLayout', component: () => import('views/EditLayoutPage.vue') },
	] },
	{ name: 'Category', routePrefix: '/themecategory', permission: 'Category', group: 'Themes', pages: [
		{ path: '/themecategory', name: 'Category', component: () => import('views/ThemeCategoriesPage.vue') },
		{ path: '/themecategory/:id', name: 'EditCategory', component: () => import('views/EditThemeCategoryPage.vue') },
	] },
	{
		name: 'Themes',
		routePrefix: '/themes',
		additionalGuardPrefixes: ['/theme'],
		permission: 'Themes',
		group: 'Themes',
		pages: [
			{ path: '/themes', name: 'Themes', component: () => import('views/ThemesPage.vue') },
			{ path: '/theme/:id', name: 'EditTheme', component: () => import('views/EditThemePage.vue') },
			{ path: '/theme/:id/page/:pageid', name: 'ThemeEditPage', component: () => import('views/ThemePageEditPage.vue') },
			{ path: '/theme/:id/page/:pageid/object/:objectid', name: 'PageObject', component: () => import('views/PageObjectEditPage.vue') },
		],
	},
	{ name: 'Templates', routePrefix: '/template', permission: 'Templates', group: 'Themes', pages: [
		{ path: '/template', name: 'Template', component: () => import('views/TemplatesPage.vue') },
		{ path: '/template/:id', name: 'EditTemplate', component: () => import('views/EditTemplatePage.vue') },
		{ path: '/template/:templateid/state/:id', name: 'EditTemplateState', component: () => import('views/EditTemplateStatePage.vue') },
		{ path: '/template/:templateid/state/:id/position/:positionid', name: 'EditTemplatePosition', component: () => import('views/EditTemplatePositionPage.vue') },
	] },
	{ name: 'Discount', routePrefix: '/discount', permission: 'Discount', group: 'Discounts', pages: [
		{ path: '/discount', name: 'Discount', component: () => import('views/DiscountsPage.vue') },
		{ path: '/discount/:id', name: 'DiscountEdit', component: () => import('views/EditDiscountPage.vue') },
	] },
	{ name: 'Discount Voucher', routePrefix: '/discount-voucher', permission: 'Discount Voucher', group: 'Discounts', pages: [
		{ path: '/discount-voucher', name: 'DiscountVoucher', component: () => import('views/DiscountVouchersPage.vue') },
	] },
	{ name: 'Shops', routePrefix: '/shop-integration', permission: 'Shop Integrations', group: 'Integrations', pages: [
		{ path: '/shop-integration', name: 'Shops', component: () => import('views/ShopsPage.vue') },
		{ path: '/shop-integration/:id', name: 'EditShop', component: () => import('views/EditShopPage.vue') },
	] },
	{ name: 'App Setting', routePrefix: '/appsetting', permission: 'App Setting', group: 'Developer', pages: [
		{ path: '/appsetting/', name: 'AppSettings', component: () => import('views/AppSettingsPage.vue') },
	] },
	{ name: 'API permission', routePrefix: '/application', permission: 'API permission', group: 'Developer', pages: [
		{ path: '/application/', name: 'ApiPermissions', component: () => import('views/ApiPermissionsPage.vue') },
		{ path: '/application/:id', name: 'EditApiPermission', component: () => import('views/EditApiPermissionPage.vue') },
	] },
] as const;

export const modulePageRoutes: readonly ModulePageRoute[] = moduleRegistry.flatMap(({ pages }) => pages);

export const routeToModuleMap: Readonly<Record<string, string>> = Object.freeze(
	Object.fromEntries(moduleRegistry.flatMap(({ routePrefix, additionalGuardPrefixes = [], permission }) => [
		[routePrefix, permission],
		...additionalGuardPrefixes.map((prefix) => [prefix, permission]),
	])),
);
